import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  Column,
  DataGrid,
  Editing,
  Export,
  HeaderFilter,
  Item,
  LoadPanel,
  Scrolling,
  SearchPanel,
  Selection,
  Sorting,
  Toolbar
} from 'devextreme-react/data-grid';
import Button from 'devextreme-react/button';
import { createStore } from 'devextreme-aspnet-data-nojquery';
import notify from 'devextreme/ui/notify';

import { getApiToken } from '../../utils/authUtils';
import { checkPermission } from '../../permissions';
import { COURSES, exportFormats } from '../../shared/constants';
import { onExporting } from '../../utils/exporter';
import './squad_list.scss';

const gridMessages = {
  insert: 'Kaderliste gespeichert',
  update: 'Kaderliste aktualisiert',
  delete: 'Kaderliste geloescht'
};

export const SquadList = () => {
  const { instance, accounts } = useMsal();
  const [dataSource, setDataSource] = useState();
  const gridRef = useRef(null);
  const navigate = useNavigate();

  const addRow = useCallback(() => {
    gridRef.current?.instance().addRow();
  }, []);

  useEffect(() => {
    getApiToken(instance, accounts).then((token) => {
      const baseUrl = `${process.env.REACT_APP_serverEndpoint}/Squad`;
      setDataSource({
        store: createStore({
          key: 'squadId',
          loadUrl: `${baseUrl}/Squads`,
          insertUrl: `${baseUrl}/InsertSquad`,
          updateUrl: `${baseUrl}/UpdateSquad`,
          deleteUrl: `${baseUrl}/DeleteSquad`,
          onBeforeSend: (method, ajaxOptions) => {
            ajaxOptions.headers = {
              Authorization: `Bearer ${token}`
            };

            if (ajaxOptions.data && ajaxOptions.data.values) {
              const values = JSON.parse(ajaxOptions.data.values);

              if (method === 'insert') {
                delete values.squadId;
              }

              ajaxOptions.data.values = JSON.stringify(values);
            }
          }
        })
      });
    });
  }, [accounts, instance]);

  const refresh = useCallback(() => {
    gridRef.current?.instance().refresh();
  }, []);

  const notifyAction = useCallback((type) => {
    const message = gridMessages[type] || 'Aenderung gespeichert';
    notify(
      {
        message,
        position: { at: 'bottom center', my: 'bottom center' }
      },
      'success',
      2000
    );
  }, []);
  const onRowClick = useCallback(({ data }) => {
    // setClubId(data.clubId);
    // setPanelOpened(true);
    const squadId = data.squadId;
    navigate(`/squad/edit/${squadId}`);
  }, [navigate]);

  return (
    <div className='view squad-list'>
      <div className='view-wrapper view-wrapper-squad-list list-page'>
        <DataGrid
          className='grid theme-dependent'
          noDataText=''
          focusedRowEnabled
          height='100%'
          dataSource={dataSource}
          ref={gridRef}
          remoteOperations
          allowColumnReordering
          showBorders
          onRowClick={onRowClick}
          onExporting={onExporting}
          onDataErrorOccurred={() => notify({ message: 'Kaderlisten konnten nicht geladen werden', position: { at: 'bottom center', my: 'bottom center' } }, 'error', 2500)}
          onRowInserted={() => { notifyAction('insert'); refresh(); }}
          onRowUpdated={() => { notifyAction('update'); refresh(); }}
          onRowRemoved={() => { notifyAction('delete'); refresh(); }}
        >
          <LoadPanel showPane={false} />
          <SearchPanel visible placeholder='Kaderliste suchen' />
          <Export enabled allowExportSelectedData formats={exportFormats} />
          <Selection
            selectAllMode='allPages'
            showCheckBoxesMode='always'
            mode='multiple'
          />
          <HeaderFilter visible />
          <Sorting mode='multiple' />
          <Scrolling mode='virtual' />
          <Editing
            mode='row'
            allowAdding={checkPermission('squad.edit')}
            allowUpdating={checkPermission('squad.edit')}
            allowDeleting={checkPermission('squad.edit')}
            useIcons
          />
          <Toolbar>
            <Item location='before'>
              <div className='grid-header'>Kaderlisten</div>
            </Item>
            <Item
              location='after'
              locateInMenu='auto'
            >
              <Button
                icon='plus'
                text='Neue Kaderliste'
                type='default'
                stylingMode='contained'
                onClick={addRow}
                disabled={!checkPermission('squad.edit')}
              />
            </Item>
            <Item
              location='after'
              locateInMenu='auto'
              showText='inMenu'
              widget='dxButton'
            >
              <Button icon='refresh' text='Refresh' stylingMode='text' onClick={refresh} />
            </Item>
            <Item location='after' locateInMenu='auto'>
              <div className='separator' />
            </Item>
            <Item name='exportButton' />
            <Item location='after' locateInMenu='auto'>
              <div className='separator' />
            </Item>
            <Item name='searchPanel' locateInMenu='auto' />
          </Toolbar>

          <Column dataField='description' caption='Bezeichnung' dataType='string' hidingPriority={2} />
          <Column dataField='description_short' caption='Kurzbezeichnung' dataType='string' hidingPriority={2} />
          <Column dataField='startdate' caption='Gueltig ab' dataType='date' format='dd.MM.yyyy' hidingPriority={1} />
          <Column dataField='enddate' caption='Gueltig bis' dataType='date' format='dd.MM.yyyy' hidingPriority={0} />
          <Column dataField='course' caption='Bahn' hidingPriority={2} lookup={{ dataSource: COURSES, valueExpr: 'id', displayExpr: 'text' }} />
          <Column dataField='sortorder' caption='Reihenfolge' dataType='number' hidingPriority={4} />


        </DataGrid>
      </div>
    </div>
  );
};
